import React from 'react';
import { useTheme } from 'styled-components';
import { ReactComponent as PlusIcon } from '../../../assets/images/PlusIcon.svg';
import { Button } from '../../../components/Button/Button';
import { CropLevelDiscountRow } from './CropLevelDiscountRow';
import { HybridLevelDiscountRow } from './HybridLevelDiscountRow';
import { IDiscountState } from './useDiscountEditing';
import { IPlanHybridDiscount } from '../../../logic/Models/Responses/CompetitiveAllowanceResponse';
import { CropKey, CropDisplay, StyledCropIcon, cropIds } from './CompetitiveAllowanceMain';
import { RoundPrice } from '../../../logic/Utility/CalculationUtilities';

export function CropLevelDiscountMatrix(props: {
	cropKey: CropKey;
	selectedCropDisplay: CropDisplay;
	isReadOnly: boolean;
	discountState: IDiscountState;
	updateCropLevelRow: (cropId: string, index: number, updatedDiscount: number, originalDiscount: number, reasonCode?: string) => void;
	deleteCropLevelRow: (cropId: string, index: number) => void;
	addCropLevelRow: (cropKey: CropKey) => void;
	updateHybridLevelRow: (hybridDiscountItem: IPlanHybridDiscount) => void;
}) 
{
	const { cropKey, selectedCropDisplay, isReadOnly, discountState, updateCropLevelRow, deleteCropLevelRow, addCropLevelRow, updateHybridLevelRow } = props;
	const theme = useTheme();

	if (selectedCropDisplay !== 'Both' && selectedCropDisplay !== cropKey) 
	{
		return null;
	}

	if (!discountState.discounts?.[cropKey].hybrids.length) 
	{
		return null;
	}

	const totalCropLevelDiscount = discountState.discounts?.[cropKey].crop.reduce((acc, item) => acc + (item.PerBagDiscount || 0), 0) || 0;
	const averagePricePerBag = RoundPrice(discountState.discounts?.[cropKey].hybrids.flatMap(h => h.Products).reduce((acc, product) => acc + (product.PricePerBag * product.Quantity), 0) /
		discountState.discounts?.[cropKey].hybrids.flatMap(h => h.Products).reduce((acc, product) => acc + product.Quantity, 0) || 0);
	const totalMsrp = discountState.discounts?.[cropKey].hybrids.flatMap(h => h.Products).reduce((acc, product) => acc + (product.PricePerBag * product.Quantity), 0) || 0;
	const totalDiscount = discountState.discounts?.[cropKey].hybrids.flatMap(h => h.Products.map(p => (totalCropLevelDiscount + h.PerBagDiscount) * p.Quantity)).reduce((acc, val) => acc + val, 0) || 0;
	const totalPriceAfterDiscount = totalMsrp - totalDiscount;

	return <div key={cropKey} className={`CompetitiveAllowance_${cropKey}Section`} style={{ marginBottom: 20 }}>
		<div className={`CompetitiveAllowance_${cropKey}Section_Header`} style={{ display: 'flex' }}>
			<StyledCropIcon cropKey={cropKey} fillcolor={theme.colors.primary} />
			<div style={{ fontSize: theme.fontSizes.large, fontWeight: theme.fontWeights.bold }}>{cropKey}</div>
			{isReadOnly ?
				<></>
				:
				<div style={{ marginLeft: 'auto' }}>
					<Button
						className={`CompetitiveAllowance_${cropKey}_AddCropDiscountButton`}
						variant='outlined'
						style={{ width: 130, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
						onClick={() => addCropLevelRow(cropKey)}
						// Disable if all reason codes are already used for crop level discounts
						disabled={discountState.discounts?.[cropKey].crop.length == discountState.reasonCodes?.[cropKey].length}
					>
						{'Crop Discount'}<PlusIcon fill={theme.colors.darkGrey} width={12} style={{ position: 'relative', top: -1, marginLeft: 'auto' }} />
					</Button>
				</div>}
		</div>
		<div className={`CompetitiveAllowance_${cropKey}_CropLevelDiscountRowsContainer`} style={{ display: 'flex', flexDirection: 'column' }}>
			{discountState.discounts?.[cropKey].crop.map((discountItem, index) => 
			{
				return (
					<CropLevelDiscountRow
						key={index}
						index={index}
						isReadOnly={isReadOnly}
						cropLevelDiscountItem={discountItem}
						cropId={cropIds[cropKey]}
						updateRow={updateCropLevelRow}
						deleteRow={deleteCropLevelRow}
						reasonCodes={discountState.reasonCodes?.[cropKey]} />
				);
			})}
		</div>
		<div className={`CompetitiveAllowance_${cropKey}_HybridLevelContainer`} style={{ display: 'flex', flexDirection: 'column', marginTop: 10 }}>
			<div
				className={`CompetitiveAllowance_${cropKey}_HybridLevelHeader`}
				style={{
					display: 'flex',
					flexDirection: 'row',
					backgroundColor: theme.colors.ghxNeutral60,
					color: theme.colors.white,
					borderColor: theme.colors.ghxNeutral60,
					borderWidth: 1,
					borderStyle: 'solid',
					borderRadius: 6,
					fontWeight: theme.fontWeights.bold,
					fontSize: theme.fontSizes.small,
					paddingLeft: 15,
					paddingRight: 15,
					textAlign: 'right'
				}}
			>
				<div style={{ width: '20%', textAlign: 'left' }}>
					Product Name
				</div>
				<div style={{ width: '5%' }}>
					Acres
				</div>
				<div style={{ width: '10%' }}>
					Price/Unit
				</div>
				<div style={{ width: '5%' }}>
					Units
				</div>
				<div style={{ width: '10%' }}>
					Subtotal
				</div>
				<div style={{ width: '15%' }}>
					Discount/Unit
				</div>
				<div style={{ width: '25%' }}>
					Reason Code
				</div>
				<div style={{ width: '12%' }}>
					Total Discount
				</div>
				<div style={{ width: '10%' }}>
					Total
				</div>
			</div>
			<div className={`CompetitiveAllowance_${cropKey}_HybridLevelHybridRowContainer`} style={{ marginTop: 5, marginBottom: 5 }}>
				{discountState.discounts?.[cropKey].hybrids.map((row, index) => 
				{
					return (
						<HybridLevelDiscountRow
							key={index}
							isReadOnly={isReadOnly}
							hybridLevelDiscountItem={row}
							updateHybridRow={updateHybridLevelRow}
							reasonCodes={discountState.reasonCodes?.[cropKey]}
							totalCropLevelDiscount={totalCropLevelDiscount} />
					);
				})}
			</div>
			<div
				className={`CompetitiveAllowance_${cropKey}_HybridLevelHybridSummary`}
				style={{
					display: 'flex',
					flexDirection: 'row',
					backgroundColor: theme.colors.mediumGrey,
					color: theme.colors.white,
					borderColor: theme.colors.mediumGrey,
					borderWidth: 1,
					borderStyle: 'solid',
					borderRadius: 6,
					fontWeight: theme.fontWeights.bold,
					fontSize: theme.fontSizes.normal,
					paddingLeft: 15,
					paddingRight: 15,
					paddingTop: 10,
					paddingBottom: 10,
					textAlign: 'right',
				}}
			>
				<div style={{ width: '25%', textAlign: 'left' }}>
					{`${cropKey} Total`}
				</div>
				<div style={{ width: '10%' }}>
					{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(averagePricePerBag)}
				</div>
				<div style={{ width: '15%' }}>
					{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalMsrp)}
				</div>
				<div style={{ width: '52%' }}>
					{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDiscount)}
				</div>
				<div style={{ width: '10%' }}>
					{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalPriceAfterDiscount)}
				</div>
			</div>
		</div>
	</div>;
}
